import OneTapModule from '@components/Modules/OneTapModule/OneTapModule';
import Head from '@components/shared/AppLayout/HtmlHead';
import type { MenuType } from '@lib/sanity/queries/menu/types';
import type { ModulesType } from '@lib/sanity/queries/modules/allModules';
import { StringProjection } from '@lib/sanity/types/common';
import cn from '@utils/cn';
import { getFirstMachineLayerData } from '@utils/machineLayerUtils';
import localFont from 'next/font/local';
import AppLinkInterceptor from './AppLinkInterceptor';
import UUIDBootstrap from './UUIDBootstrap';
import TouchCookiesBootstrap from './TouchCookiesBootstrap';

const TheFuture = localFont({
  src: [
    {
      path: '../../../../public/fonts/theFuture/TheFuture-Regular.woff2',
      weight: '400',
    },
    {
      path: '../../../../public/fonts/theFuture/TheFuture-Medium.woff2',
      weight: '500',
    },
    {
      path: '../../../../public/fonts/theFuture/TheFuture-Black.woff2',
      weight: '700',
    },
  ],
  variable: '--font-the-future',
  display: 'swap',
  preload: true,
});

export type PageData = {
  _type: string;
  menu?: MenuType;
  seo?: {
    title?: StringProjection;
    description?: StringProjection;
    schema?: string;
  };
  slug?: string;
  modules?: ModulesType[];
  _createdAt?: string;
  _updatedAt?: string;
  publishedAt?: string;
  author?: {
    seo?: {
      title?: StringProjection;
      description?: StringProjection;
      schema?: string;
    };
    slug: string;
    name?: string;
    bio?: string;
    avatar?: { asset?: { url?: string } };
  };
};

type LayoutProps = {
  children: React.ReactNode;
  lang: string;
  data?: Partial<PageData> & { _type: string };
  templates?: { title: string; url: string; imageUrl: string }[];
  posts?: { title: string; url: string; imageUrl: string }[];
  /** GTM ID for Google Tag Manager. If not provided, GTM iframe is not rendered. */
  gtmId?: string;
  /** Optional UUID computed by the server page to reuse inside layout + client bootstrap. */
  uuid?: string;
};
export default function AppLayoutClient({ children, lang, data, templates, posts, gtmId, uuid }: LayoutProps) {
  // Check if page has machine layer modules to enable over-scroll
  const pageModules = data && 'modules' in data ? data.modules : undefined;
  const firstMachineLayerData = getFirstMachineLayerData(pageModules);
  const layerVideo = firstMachineLayerData?.video;

  return (
    <html lang={lang}>
      <Head data={data} templates={templates} posts={posts} lang={lang} />
      <body
        suppressHydrationWarning
        className={cn('flex min-h-screen flex-col overflow-x-hidden bg-transparent', TheFuture.variable, {
          'overflow-y-auto': !!layerVideo?.visuals && !layerVideo?.hideOnMobile,
          'md:overflow-y-auto': !!layerVideo?.visuals && layerVideo?.hideOnMobile,
        })}
      >
        <UUIDBootstrap initialUUID={uuid} />
        <TouchCookiesBootstrap />
        <AppLinkInterceptor />
        {gtmId && (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            ></iframe>
          </noscript>
        )}

        <OneTapModule />

        {children}
      </body>
    </html>
  );
}
